/**
 * One organisation's export and the certificate its deletion leaves (docs/tenant-export.md).
 *
 * - An export is a zip of newline-delimited JSON, one file per table (`tables/<Table>.ndjson`, every
 *   row of the organisation in primary-key order), the objects the platform keeps for it
 *   (`objects/catalogue-assets/<sha256>`), and `manifest.json`, which names the schema the rows are
 *   in, counts them and gives each file's SHA-256. `aioe-tenant import` writes it back into a
 *   database; `aioe-tenant verify` checks the hashes.
 * - A deletion certificate says what went when an organisation was deleted and what remains, and
 *   carries the platform's Ed25519 signature over its canonical JSON (the keys are at
 *   `/.well-known/aioe-jwks.json`), so the customer can check it was issued here and not changed.
 */
import { z } from "zod";
export const TENANT_EXPORT_FORMAT = "aioe-organisation-export";
export const TENANT_EXPORT_FORMAT_VERSION = 1;
/** How long an export is kept before it is removed. */
export const TENANT_EXPORT_KEEP_DAYS = 7;
/** The largest archive an export writes: past it the export stops and says so. */
export const TENANT_EXPORT_MAX_BYTES = 2 * 1024 ** 3;
export const tenantExportStateSchema = z.enum(["running", "ready", "failed", "expired"]);
export const tenantExportSummarySchema = z.object({
    tables: z.number().int().nonnegative(),
    rows: z.number().int().nonnegative(),
    /** Objects copied into the archive (design systems' assets). */
    objects: z.number().int().nonnegative(),
    objectBytes: z.number().nonnegative(),
});
/** `GET /admin/v1/organisation/exports/:id`: one export as the console shows it. */
export const tenantExportSchema = z.object({
    id: z.string(),
    state: tenantExportStateSchema,
    requestedBy: z.string(),
    requestedByName: z.string().optional(),
    createdAt: z.string(),
    finishedAt: z.string().optional(),
    /** When the archive is removed (ready exports). */
    expiresAt: z.string().optional(),
    /** The archive's size in bytes and SHA-256 (hex), once ready. */
    size: z.number().optional(),
    sha256: z.string().optional(),
    summary: tenantExportSummarySchema.optional(),
    /** Why it failed, in words. */
    error: z.string().optional(),
});
/** `GET /admin/v1/organisation/exports`: newest first. */
export const tenantExportListSchema = z.object({
    exports: z.array(tenantExportSchema),
    keepDays: z.number().int(),
    maxBytes: z.number(),
    /** False on the in-memory development store, which exports a few tables only. */
    complete: z.boolean(),
});
export const tenantExportManifestTableSchema = z.object({
    name: z.string(),
    /** The file in the archive: `tables/<name>.ndjson`. */
    file: z.string(),
    rows: z.number().int().nonnegative(),
    /** Uncompressed bytes and their SHA-256 (hex). */
    bytes: z.number().int().nonnegative(),
    sha256: z.string(),
    /** Each line's keys, in order. */
    columns: z.array(z.string()),
});
export const tenantExportManifestFileSchema = z.object({
    path: z.string(),
    kind: z.literal("catalogue-asset"),
    /** The object's own id (an asset's id is the SHA-256 of its bytes). */
    id: z.string(),
    bytes: z.number().int().nonnegative(),
    sha256: z.string(),
});
/** Objects the organisation has in storage that the archive lists but does not carry, and why. */
export const tenantExportNotCopiedSchema = z.object({
    what: z.string(),
    count: z.number().int().nonnegative(),
    bytes: z.number().nonnegative().optional(),
    /** Where they are: the key pattern in the object store. */
    keys: z.string(),
    why: z.string(),
});
export const tenantExportManifestSchema = z.object({
    format: z.literal(TENANT_EXPORT_FORMAT),
    formatVersion: z.literal(TENANT_EXPORT_FORMAT_VERSION),
    /** The latest database migration the rows were read under (`prisma/migrations/<name>`). */
    schemaVersion: z.string(),
    aioeVersion: z.string(),
    edition: z.enum(["enterprise", "cloud"]),
    /** postgres: every table; memory: the development store's few. */
    store: z.enum(["postgres", "memory"]),
    complete: z.boolean(),
    organisation: z.object({ id: z.string(), slug: z.string(), name: z.string(), kind: z.string() }),
    exportedAt: z.string(),
    exportedBy: z.object({ userId: z.string().optional(), name: z.string().optional(), email: z.string().optional(), via: z.enum(["console", "cli"]) }),
    tables: z.array(tenantExportManifestTableSchema),
    files: z.array(tenantExportManifestFileSchema),
    notCopied: z.array(tenantExportNotCopiedSchema),
    /** How sealed values and tokens are carried. */
    secrets: z.string(),
    notes: z.array(z.string()),
});
/* ---------- deletion certificates ---------- */
export const DELETION_CERTIFICATE_TYP = "aioe-deletion-certificate+jws";
export const deletionCertificateSchema = z.object({
    type: z.literal("aioe-deletion-certificate"),
    version: z.literal(1),
    id: z.string(),
    edition: z.enum(["enterprise", "cloud"]),
    organisation: z.object({ id: z.string(), name: z.string(), kind: z.string().optional() }),
    requestedBy: z.object({ userId: z.string().optional(), name: z.string().optional(), email: z.string().optional() }),
    requestedAt: z.string(),
    completedAt: z.string(),
    deleted: z.object({
        /** Rows deleted, by table. */
        rows: z.record(z.string(), z.number().int().nonnegative()),
        totalRows: z.number().int().nonnegative(),
        /** Objects removed from storage, and the bytes of those whose size was recorded. */
        objects: z.object({ count: z.number().int().nonnegative(), bytes: z.number().nonnegative(), failed: z.number().int().nonnegative() }),
    }),
    /** What is still held after the deletion, and why. */
    remains: z.array(z.object({ what: z.string(), why: z.string() })),
});
/** The certificate as downloaded: the certificate, and the platform's signature over its canonical JSON. */
export const deletionCertificateDocumentSchema = z.object({
    certificate: deletionCertificateSchema,
    signature: z.object({
        alg: z.literal("EdDSA"),
        kid: z.string(),
        /** SHA-256 (base64url) of the certificate's canonical JSON (keys sorted), which the JWS's claims name. */
        sha256: z.string(),
        /** A compact JWS (typ aioe-deletion-certificate+jws) whose claims are { certificateId, sha256 }. */
        jws: z.string(),
    }),
    /** How to check it, in words. */
    verify: z.string(),
});
/** `GET /admin/v1/organisation/deletion-certificates`: the certificates the caller asked for. */
export const deletionCertificateListSchema = z.object({
    certificates: z.array(z.object({ id: z.string(), organisation: z.string(), completedAt: z.string() })),
});
//# sourceMappingURL=tenantExport.js.map